/**
 * 快速发布项目到 App 弹窗
 * 
 * v11.2.2 重构：
 * - 左右分栏布局：左边预览，右边配置
 * - 新增角色展示信息：avatar_url、display_name、subtitle、description
 * - 新增标签输入
 * - 图片上传功能
 */

import { useState, useEffect, useCallback, KeyboardEvent } from 'react'
import { Loader2, Package, Check, ChevronRight, Crown, X, Plus, ImageIcon } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Separator } from '@/shared/components/ui/separator'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { useToast } from '@/shared/hooks/useToast'
import { AppApiService, CategoryApiService, ProjectPublishApiService } from '../services/app-api'
import type { AppListItem, Category, PublishProjectRequest } from '../types/app'
import AvatarUpload from './AvatarUpload'
import PhonePreview from './PhonePreview'

interface QuickPublishToAppDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  projectName: string
  projectDescription?: string
  onSuccess: () => void
}

// 发布配置状态类型
interface PublishConfig {
  // 角色展示信息（v11.2.2 新增）
  avatar_url: string
  display_name: string
  subtitle: string
  description: string
  tags: string[]
  
  // 发布配置
  subscription_tier: 'free' | 'tier1' | 'tier2'
  category: string
  
  // 试用配置（0 表示使用 App 默认）
  trial_count: number
  trial_duration_seconds: number
  
  // 推荐配置
  is_recommended: boolean
  recommend_sort_order: number
}

export default function QuickPublishToAppDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  projectDescription = '',
  onSuccess,
}: QuickPublishToAppDialogProps) {
  const { showSuccess, showError } = useToast()

  // 步骤状态
  const [step, setStep] = useState<'select-app' | 'config'>('select-app')

  // App 选择状态
  const [apps, setApps] = useState<AppListItem[]>([])
  const [isLoadingApps, setIsLoadingApps] = useState(false)
  const [selectedApp, setSelectedApp] = useState<AppListItem | null>(null)

  // 分类状态
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoadingCategories, setIsLoadingCategories] = useState(false)

  // 发布配置状态
  const [config, setConfig] = useState<PublishConfig>({
    avatar_url: '',
    display_name: '',
    subtitle: '',
    description: '',
    tags: [],
    subscription_tier: 'free',
    category: '',
    trial_count: 0,
    trial_duration_seconds: 0,
    is_recommended: false,
    recommend_sort_order: 0,
  })

  // 标签输入状态
  const [tagInput, setTagInput] = useState('')

  // 上传状态
  const [isUploading, setIsUploading] = useState(false)

  // 发布状态
  const [isPublishing, setIsPublishing] = useState(false)

  // 加载 App 列表
  const loadApps = useCallback(async () => {
    setIsLoadingApps(true)
    try {
      const response = await AppApiService.getApps()
      if (response.success && response.data?.apps) {
        setApps(response.data.apps)
      }
    } catch (error) {
      console.error('❌ [QuickPublishDialog] 加载 App 列表失败:', error)
      showError('加载 App 列表失败')
    } finally {
      setIsLoadingApps(false)
    }
  }, [showError])

  // 加载分类列表
  const loadCategories = useCallback(async (appId: string) => {
    setIsLoadingCategories(true)
    try {
      const response = await CategoryApiService.getCategories(appId)
      if (response.success && response.data?.categories) {
        setCategories(response.data.categories)
      }
    } catch (error) {
      console.error('❌ [QuickPublishDialog] 加载分类列表失败:', error)
      // 不显示错误，分类可能为空
    } finally {
      setIsLoadingCategories(false)
    }
  }, [])

  // 打开时初始化
  useEffect(() => {
    if (open) {
      setStep('select-app')
      setSelectedApp(null)
      setCategories([])
      setTagInput('')
      // 初始化配置，使用项目名和描述作为默认值
      setConfig({
        avatar_url: '',
        display_name: projectName,
        subtitle: '',
        description: projectDescription,
        tags: [],
        subscription_tier: 'free',
        category: '',
        trial_count: 0,
        trial_duration_seconds: 0,
        is_recommended: false,
        recommend_sort_order: 0,
      })
      loadApps()
    }
  }, [open, projectName, projectDescription, loadApps])

  // 选择 App 后加载分类
  useEffect(() => {
    if (selectedApp) {
      loadCategories(selectedApp.app_id)
    }
  }, [selectedApp, loadCategories])

  // 选择 App
  const handleSelectApp = (app: AppListItem) => {
    setSelectedApp(app)
    setStep('config')
  }

  // 返回 App 选择
  const handleBackToSelect = () => {
    setStep('select-app')
    setSelectedApp(null)
    setCategories([])
  }

  // 添加标签
  const handleAddTag = () => {
    const tag = tagInput.trim()
    if (tag && !config.tags.includes(tag) && config.tags.length < 10) {
      setConfig(prev => ({ ...prev, tags: [...prev.tags, tag] }))
      setTagInput('')
    }
  }

  // 标签输入按回车添加
  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    }
  }

  // 移除标签
  const handleRemoveTag = (tagToRemove: string) => {
    setConfig(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  // 发布项目
  const handlePublish = async () => {
    if (!selectedApp) return

    // 校验必填字段
    if (!config.avatar_url) {
      showError('请先上传角色形象图片')
      return
    }

    setIsPublishing(true)
    try {
      const request: PublishProjectRequest = {
        app_id: selectedApp.app_id,
        
        // 角色展示信息（v11.2.2）
        avatar_url: config.avatar_url,
        display_name: config.display_name.trim() || undefined,
        subtitle: config.subtitle.trim() || undefined,
        description: config.description.trim() || undefined,
        tags: config.tags.length > 0 ? config.tags : undefined,
        
        // 发布配置
        subscription_tier: config.subscription_tier,
        category: config.category || undefined,
        
        // 试用配置（0 表示使用 App 默认，不传）
        trial_count: config.trial_count > 0 ? config.trial_count : undefined,
        trial_duration_seconds: config.trial_duration_seconds > 0 ? config.trial_duration_seconds : undefined,
        
        // 推荐配置
        is_recommended: config.is_recommended,
        recommend_sort_order: config.is_recommended ? config.recommend_sort_order : undefined,
      }

      const response = await ProjectPublishApiService.publishProject(projectId, request)
      if (response.success) {
        showSuccess(`项目已发布到 ${selectedApp.app_name}`)
        onSuccess()
        onOpenChange(false)
      } else {
        showError('发布失败，请重试')
      }
    } catch (error: unknown) {
      console.error('❌ [QuickPublishDialog] 发布失败:', error)
      const errorMessage = error instanceof Error ? error.message : '发布失败，请重试'
      showError(errorMessage)
    } finally {
      setIsPublishing(false)
    }
  }

  // 更新配置字段的便捷方法
  const updateConfig = <K extends keyof PublishConfig>(key: K, value: PublishConfig[K]) => {
    setConfig(prev => ({ ...prev, [key]: value }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={step === 'config' ? 'sm:max-w-[900px] h-[85vh] flex flex-col' : 'sm:max-w-[500px]'}>
        <DialogHeader>
          <DialogTitle>
            {step === 'select-app' ? '发布到 App' : `发布到 ${selectedApp?.app_name}`}
          </DialogTitle>
          <DialogDescription>
            {step === 'select-app' 
              ? `将项目 "${projectName}" 发布到 App，供用户使用`
              : '配置角色展示信息和发布参数'
            }
          </DialogDescription>
        </DialogHeader>

        {step === 'select-app' ? (
          // 步骤1：选择 App
          <div className="py-4">
            {isLoadingApps ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : apps.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>暂无可用的 App</p>
                <p className="text-sm mt-1">请先在 App 管理中创建 App</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {apps.map(app => (
                  <button
                    key={app.app_id}
                    onClick={() => handleSelectApp(app)}
                    className="w-full p-3 rounded-lg border hover:bg-accent hover:border-primary transition-colors flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-3">
                      {app.icon_url ? (
                        <img src={app.icon_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                          <Package className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{app.app_name}</div>
                        <div className="text-sm text-muted-foreground">{app.company}</div>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          // 步骤2：配置发布参数（左右分栏布局）
          <div className="flex gap-6 py-4 flex-1 min-h-0 overflow-hidden h-full">
            {/* 左侧：预览区 */}
            <div className="flex-shrink-0 flex flex-col items-center gap-4 overflow-y-auto max-h-full">
              {/* 图片上传 */}
              {!config.avatar_url ? (
                <AvatarUpload
                  projectId={projectId}
                  value={config.avatar_url}
                  onChange={(url) => updateConfig('avatar_url', url)}
                  onUploadStart={() => setIsUploading(true)}
                  onUploadEnd={() => setIsUploading(false)}
                  onError={(msg) => showError(msg)}
                  className="w-[240px]"
                />
              ) : (
                <PhonePreview
                  avatarUrl={config.avatar_url}
                  displayName={config.display_name || projectName}
                  subtitle={config.subtitle}
                  description={config.description}
                  tags={config.tags}
                  isRecommended={config.is_recommended}
                />
              )}
              
              {/* 更换图片按钮 */}
              {config.avatar_url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateConfig('avatar_url', '')}
                  disabled={isUploading}
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  更换图片
                </Button>
              )}
            </div>

            {/* 右侧：配置表单 */}
            <ScrollArea className="flex-1 pr-4 max-h-full">
              <div className="space-y-6">
                {/* 目标 App */}
                <div className="p-3 bg-muted/50 rounded-lg flex items-center gap-3">
                  {selectedApp?.icon_url ? (
                    <img src={selectedApp.icon_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                      <Package className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-medium">{selectedApp?.app_name}</div>
                    <div className="text-xs text-muted-foreground">目标 App</div>
                  </div>
                </div>

                <Separator />

                {/* 角色信息 */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <span className="w-1 h-4 bg-primary rounded-full" />
                    角色信息
                  </h4>

                  {/* 角色名称 */}
                  <div className="space-y-2">
                    <Label htmlFor="display_name">角色名称</Label>
                    <Input
                      id="display_name"
                      placeholder={projectName}
                      value={config.display_name}
                      onChange={(e) => updateConfig('display_name', e.target.value)}
                      maxLength={30}
                    />
                    <p className="text-xs text-muted-foreground">
                      留空则使用项目名称
                    </p>
                  </div>

                  {/* 角色定位 */}
                  <div className="space-y-2">
                    <Label htmlFor="subtitle">角色定位</Label>
                    <Input
                      id="subtitle"
                      placeholder="例如：你的灵魂画师"
                      value={config.subtitle}
                      onChange={(e) => updateConfig('subtitle', e.target.value)}
                      maxLength={50}
                    />
                  </div>

                  {/* 角色介绍 */}
                  <div className="space-y-2">
                    <Label htmlFor="description">角色介绍</Label>
                    <Textarea
                      id="description"
                      placeholder="介绍这个角色的特点和风格..."
                      value={config.description}
                      onChange={(e) => updateConfig('description', e.target.value)}
                      maxLength={200}
                      rows={3}
                    />
                  </div>

                  {/* 标签 */}
                  <div className="space-y-2">
                    <Label>标签</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="输入标签按回车添加"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagKeyDown}
                        maxLength={20}
                        disabled={config.tags.length >= 10}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleAddTag}
                        disabled={!tagInput.trim() || config.tags.length >= 10}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {config.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {config.tags.map((tag, index) => (
                          <Badge key={index} variant="secondary" className="gap-1 pr-1">
                            {tag}
                            <button
                              type="button"
                              onClick={() => handleRemoveTag(tag)}
                              className="ml-1 hover:bg-muted rounded-full p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      最多 10 个标签，当前 {config.tags.length}/10
                    </p>
                  </div>
                </div>

                <Separator />

                {/* 发布配置 */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <span className="w-1 h-4 bg-primary rounded-full" />
                    发布配置
                  </h4>

                  {/* 订阅等级 */}
                  <div className="space-y-2">
                    <Label>订阅等级</Label>
                    <Select
                      value={config.subscription_tier}
                      onValueChange={(value: 'free' | 'tier1' | 'tier2') => 
                        updateConfig('subscription_tier', value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">
                          <div className="flex items-center gap-2">
                            免费
                          </div>
                        </SelectItem>
                        <SelectItem value="tier1">
                          <div className="flex items-center gap-2">
                            <Crown className="h-4 w-4 text-yellow-500" />
                            付费 Tier 1
                          </div>
                        </SelectItem>
                        <SelectItem value="tier2">
                          <div className="flex items-center gap-2">
                            <Crown className="h-4 w-4 text-purple-500" />
                            付费 Tier 2
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* 分类 */}
                  <div className="space-y-2">
                    <Label>分类</Label>
                    {isLoadingCategories ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        加载中...
                      </div>
                    ) : (
                      <Select
                        value={config.category || '__none__'}
                        onValueChange={(value) => 
                          updateConfig('category', value === '__none__' ? '' : value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="选择分类（可选）" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">不指定分类</SelectItem>
                          {categories.map(cat => (
                            <SelectItem key={cat.category_id} value={cat.category_id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* 试用配置 */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">试用配置</Label>
                      <span className="text-xs text-muted-foreground">0 = 使用 App 默认</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="trial_count" className="text-xs text-muted-foreground">
                          试用次数
                        </Label>
                        <Input
                          id="trial_count"
                          type="number"
                          min={0}
                          max={100}
                          placeholder="0"
                          value={config.trial_count || ''}
                          onChange={(e) => updateConfig('trial_count', parseInt(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="trial_duration" className="text-xs text-muted-foreground">
                          试用时长（秒）
                        </Label>
                        <Input
                          id="trial_duration"
                          type="number"
                          min={0}
                          max={86400}
                          placeholder="0"
                          value={config.trial_duration_seconds || ''}
                          onChange={(e) => updateConfig('trial_duration_seconds', parseInt(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* 推荐设置 */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>设为推荐</Label>
                      <p className="text-xs text-muted-foreground">
                        推荐项目会在 App 首页展示
                      </p>
                    </div>
                    <Switch
                      checked={config.is_recommended}
                      onCheckedChange={(checked) => updateConfig('is_recommended', checked)}
                    />
                  </div>

                  {/* 推荐排序 */}
                  {config.is_recommended && (
                    <div className="space-y-2">
                      <Label htmlFor="recommend_sort_order">推荐排序</Label>
                      <Input
                        id="recommend_sort_order"
                        type="number"
                        min={0}
                        max={999}
                        placeholder="数字越小越靠前"
                        value={config.recommend_sort_order || ''}
                        onChange={(e) => updateConfig('recommend_sort_order', parseInt(e.target.value) || 0)}
                      />
                      <p className="text-xs text-muted-foreground">
                        数字越小排序越靠前
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          </div>
        )}

        <DialogFooter className="flex-shrink-0">
          {step === 'select-app' ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleBackToSelect} disabled={isPublishing}>
                返回
              </Button>
              <Button 
                onClick={handlePublish} 
                disabled={isPublishing || isUploading || !config.avatar_url}
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    发布中...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    确认发布
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
